import { useCallback, useEffect } from 'react';

import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';
import type { RovoContentBridgeSetupHandler } from '@atlassian/rovo-content-bridge-api/client';
import { client } from '@atlassian/rovo-content-bridge-api';
import { usePreviewPanelHandler } from '@confluence/whiteboard-common/entry-points/usePreviewPanelHandler';
import { expVal } from '@confluence/feature-experiments';

const CONFLUENCE_AFM_CLIENT_ID = client('confluence-afm');

/**
 * Hook that listens to the Rovo content bridge for databases.
 */
export const useDatabaseRovoContentBridge = ({
	enabled = true,
}: {
	enabled?: boolean;
} = {}): {
	isReady: boolean;
	send: any;
	teardown: () => void;
} => {
	// Bind the preview panel handler
	const bindPreviewPanelHandler = usePreviewPanelHandler();
	const setupHandler: RovoContentBridgeSetupHandler = useCallback(
		(on) => {
			if (expVal('preview_panel_for_databases', 'isEnabled', false)) {
				bindPreviewPanelHandler(on);
			}

			// You can add more command listeners here as needed
			// on('SomeOtherCommand', handleSomeOtherCommand);
		},
		[bindPreviewPanelHandler],
	);

	const { ready, send, teardown } = useRovoContentBridgeAPI(CONFLUENCE_AFM_CLIENT_ID, setupHandler);

	// Initialize the bridge when enabled
	useEffect(() => {
		if (!enabled) {
			return;
		}

		ready();

		// Cleanup when the component unmounts or when disabled
		return () => {
			teardown();
		};
	}, [ready, teardown, enabled]);

	// Return utility functions for manual service triggering if needed
	return {
		isReady: enabled,
		send,
		teardown,
	};
};
